import { ArrowBack } from "@mui/icons-material";
import { Box, IconButton, Paper, Stack, Typography, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import { MdLocationOn, MdPhoneAndroid } from "react-icons/md";
import { useParams, useNavigate } from "react-router-dom";
import { getCustomerDetails, getTrayHistory, getTrays } from "../../actions";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LOGO_BASE64 from "../../assets/data/logoBase64";
import { useAuth } from "../../context/AuthContext";

// Static data for tray history
const trayHistoryData = [
  {
    date: "2024-01-15",
    trayHistory: {
      IN: [
        { name: "Small Tray", count: 5 },
        { name: "Medium Tray", count: 3 },
        { name: "Large Tray", count: 2 },
      ],
      OUT: [
        { name: "Small Tray", count: 2 },
        { name: "Medium Tray", count: 1 },
      ],
    },
    total: 13,
  },
  {
    date: "2024-01-14",
    trayHistory: {
      IN: [
        { name: "Small Tray", count: 8 },
        { name: "Large Tray", count: 4 },
      ],
      OUT: [
        { name: "Small Tray", count: 3 },
        { name: "Medium Tray", count: 2 },
        { name: "Large Tray", count: 1 },
      ],
    },
    total: 18,
  },
  {
    date: "2024-01-13",
    trayHistory: {
      IN: [
        { name: "Medium Tray", count: 6 },
        { name: "Large Tray", count: 3 },
      ],
      OUT: [
        { name: "Small Tray", count: 4 },
        { name: "Medium Tray", count: 2 },
      ],
    },
    total: 15,
  },
  {
    date: "2024-01-12",
    trayHistory: {
      IN: [
        { name: "Small Tray", count: 10 },
        { name: "Medium Tray", count: 5 },
        { name: "Large Tray", count: 3 },
      ],
      OUT: [
        { name: "Small Tray", count: 5 },
        { name: "Large Tray", count: 2 },
      ],
    },
    total: 25,
  },
];

function TrayHistory() {
  const navigate = useNavigate();
  const params = useParams();

  const [customer, setCustomer] = useState({});
  const [trayHistory, setTrayHistory] = useState([]);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [trayTypes, setTrayTypes] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    const func = async () => {
      const customerDetails = await getCustomerDetails(params?.id);
      let startDate
      let endDate
      if(dateRange.startDate){
        startDate = dayjs(dateRange.startDate).startOf("day").toISOString();
      }
      if(dateRange.endDate){
        endDate = dayjs(dateRange.endDate).startOf("day").toISOString();
      }
      const trayHistory = await getTrayHistory(params?.id, startDate, endDate);
      const traysList = await getTrays();
      setCustomer(customerDetails);
      setTrayHistory(trayHistory);
      setTrayTypes(Array.isArray(traysList) ? traysList : []);
    };
    func();
  }, [params?.id, dateRange]);

  const getTrayNameById = (typeId) => {
    const tray = trayTypes.find(
      (tray) =>
        String(tray?.trayTypeId) === String(typeId) ||
        String(tray?.id) === String(typeId)
    );
    if (!tray) return "";

    return tray?.label;
  };

  const clearDateFilter = () => {
    setDateRange({
      startDate: null,
      endDate: null,
    });
  };

  const mergeTrayCounts = (items = []) => {
    const grouped = {};

    items.forEach(item => {
      if (!item?.trayTypeId) return;

      if (!grouped[item.trayTypeId]) {
        grouped[item.trayTypeId] = 0;
      }

      grouped[item.trayTypeId] += Number(item.count || 0);
    });

    return Object.keys(grouped).map(key => ({
      trayTypeId: key,
      count: grouped[key]
    }));
  };


  const printPDF = () => {
    const printWindow = window.open("", "_blank");

    const rows = trayHistory.map(item => {
      const dateFormatted = new Date(item.date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "Asia/Kolkata"
      });

      const inItems = mergeTrayCounts(item.IN || []);
      const outItems = mergeTrayCounts(item.OUT || []);

      const maxLength = Math.max(inItems.length, outItems.length);

      let tableRows = "";

      for (let i = 0; i < maxLength; i++) {
        const inItem = inItems[i];
        const outItem = outItems[i];

        tableRows += `
          <tr>
            ${i === 0 ? `<td rowspan="${maxLength}">${dateFormatted}</td>` : "--"}

            <!-- IN Block -->
            <td>${inItem ? getTrayNameById(inItem.trayTypeId) : "--"}</td>
            <td>${inItem ? inItem.count : "--"}</td>
            ${i === 0 ? `<td rowspan="${maxLength}" style="font-weight:bold;">${item.totalIn || 0}</td>` : "--"}

            <!-- OUT Block -->
            <td>${outItem ? getTrayNameById(outItem.trayTypeId) : "--"}</td>
            <td>${outItem ? outItem.count : "--"}</td>
            ${i === 0 ? `<td rowspan="${maxLength}" style="font-weight:bold;">${item.totalOut || 0}</td>` : "--"}
          </tr>
        `;
      }

      return tableRows;
    }).join("");

    const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Tray History</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 30px;
        }

        h2 {
          text-align: center;
          margin-bottom: 10px;
        }

        .customer-info {
          margin-bottom: 20px;
          font-size: 14px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        th, td {
          border: 1px solid #000;
          padding: 6px;
          text-align: center;
        }

        th {
          background: #f2f2f2;
        }

        .total-cell {
          font-weight: bold;
          background: #f9f9f9;
        }

        .proprietor-meta { font-size: 13px; color: #555; margin-top: 4px; }

        @media print {
          body {
            margin: 10mm;
          }
        }
      </style>
    </head>

    <body>
      <div class="header" style="display: flex; align-items: flex-start; justify-content: space-between">
        <div style="display: flex; align-items: center; gap: 12px">
          <img
            src=${LOGO_BASE64}
            alt="DBF Logo"
            style="width: 50px; height: 50px; border-radius: 4px"
          />
          <div>
            <div
              style="font-size: 20px; font-weight: bold; color: #1976d2;">
              ${user?.employerName}
            </div>
            <div style="color: #666; font-size: 16px">
              ${customer?.branchName}
            </div>
            <div class="proprietor-meta">
              Proprietor: <strong>Maddireddy Ramesh</strong> &bull; Contact: <strong>+91 76666 77779</strong>
            </div>
          </div>
        </div>
        <div class="customer-info">
          <strong>Customer:</strong> ${customer?.name || "-"} <br/>
          <strong>Location:</strong> ${customer?.location || "-"} <br/>
          <strong>Generated at:</strong> ${new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
            timeZone: "Asia/Kolkata"
          })}
        </div>
      </div>
      <h2>Tray History</h2>
      <table>
        <thead>
          <tr>
            <th rowspan="2">Date</th>
            <th colspan="3">IN</th>
            <th colspan="3">OUT</th>
          </tr>
          <tr>
            <th>Item</th>
            <th>Count</th>
            <th>Total</th>
            <th>Item</th>
            <th>Count</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>

    </body>
    </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };





  return (
    <>
      <Paper
        elevation={2}
        sx={{ p: 2, mb: 0, borderRadius: 0, backgroundColor: "#fafafa" }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Stack
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap={1}
            >
              <IconButton onClick={() => navigate(-1)} sx={{ p: 0 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" fontWeight="bold">
                {customer?.name}
              </Typography>
            </Stack>
            <Stack
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap={3}
            >
              <Typography
                color="text.secondary"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <MdPhoneAndroid size={18} />
                +91 {customer?.mobile}
              </Typography>
              <Typography
                color="text.secondary"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <MdLocationOn size={18} />
                {customer?.location}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Paper>

      <Box  display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={2}
        mt={3}
        pr={3}
        width="100%">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box display="flex" alignItems="center" gap={1}>
            <DatePicker
              label="From"
              value={dateRange.startDate}
              maxDate={dateRange.endDate}
              onChange={(v) => {
                if (!v || !v.isValid()) return;
                setDateRange((p) => ({ ...p, startDate: v }));
              }}
              slotProps={{
                textField: { size: "small", sx: { width: 160 } },
              }}
            />

            <DatePicker
              label="To"
              value={dateRange.endDate}
              minDate={dateRange.startDate}
              maxDate={dayjs()}
              onChange={(v) => {
                if (!v || !v.isValid()) return;
                setDateRange((p) => ({ ...p, endDate: v }));
              }}
              slotProps={{
                textField: { size: "small", sx: { width: 160 } },
              }}
            />

            {/*CLEAR BUTTON */}
            <Button
              size="small"
              variant="text"
              onClick={clearDateFilter}
              disabled={!dateRange.startDate && !dateRange.endDate}
            >
              Reset
            </Button>
          </Box>
        </LocalizationProvider>

        <Button
          variant="outlined"
          onClick={printPDF}
          disabled={trayHistory.length == 0}
        >
          Print PDF
        </Button>

      </Box>

      <div className="p-6 bg-white min-h-screen">
        {/* Table Header */}
        {/* <div className="flex border-b-2 border-gray-300 bg-white">
          <div className="w-[100px] p-3 font-semibold text-gray-700 border-r border-gray-200">
            Date
          </div>
          <div className="p-3 font-semibold text-gray-700 border-r border-gray-200 w-[calc(100%-200px)]">
            Tray History
          </div>
          <div className="w-[100px] p-3 font-semibold text-gray-700 text-center">
            Total
          </div>
        </div> */}

        {/* Table Rows */}
        <div className="">
          {trayHistory.map((item, index) => (
            <>
            {(item?.IN?.length > 0 || item?.OUT?.length > 0) && <div key={index}>
              {/* Date Column */}
              <div className=" p-1 border-gray-200 flex items-center ">
                <span className="text-md font-semibold text-gray-800">
                  {new Date(item.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    timeZone: "Asia/Kolkata",
                  })}
                </span>
              </div>
              <div className="flex rounded-md transition-colors mb-4 bg-white ">
                {/* Tray History Column */}
                <div className="  w-full">
                  <div className="rounded-md overflow-hidden">
                    {/* IN Section */}
                    {mergeTrayCounts(item.IN).length > 0 && (
                      <div className="p-0 ">
                        <div className="flex">
                          {/* <span className="w-2 h-2 bg-green-500 rounded-full"></span> */}
                          <span className="flex items-center justify-center text-sm font-bold border-r text-black uppercase bg-[#e4ffe4] px-4 rounded-tl-md min-w-[80px]">
                            IN
                          </span>
                          <div className="flex flex-wrap gap-3 p-2 w-[calc(100%-180px)] bg-[#e4ffe4]">
                            {mergeTrayCounts(item.IN).map((tray, trayIndex) => (
                              <div
                                key={trayIndex}
                                className="flex items-center text-md gap-2 border border-gray-200 bg-white rounded-md"
                              >
                                <span className=" text-[13px] pl-2 font-medium text-gray-700 pl-2">
                                  {getTrayNameById(tray.trayTypeId)}
                                </span>
                                <span className="font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-r-md h-full flex items-center justify-center text-[14px]">
                                  {tray.count}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="min-w-[100px] p-3 flex items-center justify-center bg-[#e4ffe4] border-l border-gray-250 ">
                            <span className="text-lg font-bold text-gray-500">
                              {item.totalIn}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* OUT Section */}
                    {mergeTrayCounts(item.OUT).length > 0 && (
                      <div className="p-0">
                        <div className="flex  ">
                          {/* <span className="w-2 h-2 bg-red-500 rounded-full"></span> */}
                          <span className=" flex items-center justify-center text-sm font-bold text-black border-r uppercase bg-[#f5f5f5] px-4 rounded-bl-md min-w-[80px]">
                            OUT
                          </span>
                          <div className="flex flex-wrap gap-3 p-2 w-[calc(100%-180px)] bg-[#f5f5f5]">
                            {mergeTrayCounts(item.OUT).map((tray, trayIndex) => (
                              <div
                                key={trayIndex}
                                className="flex items-center text-md gap-2 border border-gray-200 bg-white rounded-md"
                              >
                                <span className="text-black text-[13px] pl-2 font-medium">
                                  {getTrayNameById(tray.trayTypeId)}
                                </span>
                                <span className="font-semibold text-red-700 text-[14px] bg-red-100 px-2 py-0.5 rounded-r-md h-full flex items-center justify-center">
                                  {tray.count}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="min-w-[100px] p-3 flex items-center justify-center bg-[#f5f5f5] border-l border-gray-250">
                            <span className="text-lg font-bold text-blue-500">
                              {item.totalOut}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Total Column */}
              </div>
            </div>}
            </>
          ))}
        </div>
      </div>
    </>
  );
}

export default TrayHistory;
